import * as React from 'react';
import { useOrganization, useMe } from '~/libs/api-hooks/api/private';
import { useEffect, useMemo } from 'react';
import {
  EventProperties,
  EventPropertiesSnakeCase,
} from '~/libs/analytics/types/EventProperties';
import mapCamelCaseObjectToSnakeCase from '~/libs/analytics/utils/mapCamelCaseObjectToSnakeCase';
import useAnalytics from '~/libs/analytics/hooks/useAnalytics';
import exceptionLogger from '~/libs/exception-logger';
import {
  camelCaseToKebabCase,
  kebabCase,
} from '~/libs/analytics/utils/caseConversion';
import { AnalyticsContextType } from './Analytics';
import eventPropertiesHasUndefinedValue from '~/libs/analytics/utils/eventPropertiesHasUndefinedValue';
import global from '~/libs/analytics/utils/global';
import useFingerprint from '~/libs/analytics/hooks/useFingerprint';

export interface PageContextType {
  pageType: 'inner' | 'outer';
  pageName: string | null;
  teamName: string | null;
  organizationId: string | undefined | null;
  userId: string | undefined | null;
  anonymousId: string | null | undefined;
}

interface PageOptions {
  server?: boolean;
}

export const PageContext = React.createContext<PageContextType>({
  pageType: 'outer',
  pageName: null,
  teamName: null,
  organizationId: null,
  userId: null,
  anonymousId: null,
});

interface PageProps
  extends Omit<PageContextType, 'organizationId' | 'userId' | 'anonymousId'> {
  orgSlug: string | undefined | null;
  children?: React.ReactNode;
  eventProperties?: Partial<
    Omit<EventProperties, 'pageType' | 'pageName' | 'teamName'>
  >;
  options?: PageOptions;
}

/**
 * Page component to wrap the contents of a page.
 * Any useTrackAction will be able to use the page data gathered by this component.
 * Will send the track page event.
 * @param {'inner' | 'outer'} pageType - Records the type of page the segment event was collected on.
 * @param {camelCase | kebab-case string} pageName - Records the name of page the segment event was collected on.
 * @param {string} teamName - Records the name of the engineering team responsible for adding the event.
 * @param {string | undefined | null} orgSlug - slug that represents the current org. Set to null if there is no current organization. Undefined can be used to show that we are waiting for the data to be loaded
 * @param {EventProperties} [eventProperties] - Extra properties to send when the page event is called.
 * @param {PageOptions} [options] - Currently there is only one option. Server determines if track page event should go through server. Try not to send through server and this option respects privacy.
 */
const Page = ({
  pageType,
  pageName,
  teamName,
  orgSlug,
  eventProperties = {},
  options = {},
  children,
}: PageProps): React.ReactElement => {
  const analytics = useAnalytics();
  const {
    data: me,
    isSuccess: isMeSuccess,
    isError: isMeError,
    error: meError,
  } = useMe({
    reactQueryOptions: {
      retry: false,
      retryOnMount: false,
      enabled: pageType === 'inner',
    },
  });

  const shouldNotHaveAUser = useMemo<boolean>(() => {
    return Boolean(
      pageType === 'outer' || (isMeError && meError?.isUnauthorizedError()),
    );
  }, [pageType, meError, isMeError]);

  const shouldGetOrganizationId = useMemo<boolean>(
    () => orgSlug !== null,
    [orgSlug],
  );

  const { data: organization, isSuccess: isOrganizationSuccess } =
    useOrganization({
      orgSlug: orgSlug ?? '',
      reactQueryOptions: { enabled: !!orgSlug },
    });

  const fingerprint = useFingerprint();

  const PageDetails = useMemo((): PageContextType => {
    const pageNameKebab = camelCaseToKebabCase(pageName ?? '');
    if (pageName !== null && !kebabCase.test(pageNameKebab)) {
      exceptionLogger().error(
        `pageName needs to be camelCase or kebab-case, PageName: ${pageName}`,
        {
          pageName,
          pageType,
          teamName,
        },
      );
    }
    const user = analytics?.segment?.user();
    return {
      pageType,
      pageName:
        pageName && kebabCase.test(pageNameKebab) ? pageNameKebab : null,
      teamName,
      organizationId: shouldGetOrganizationId ? organization?.id : null,
      userId: !shouldNotHaveAUser ? me?.analyticsId : null,
      anonymousId:
        typeof user?.anonymousId === 'function'
          ? user.anonymousId()
          : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageName,
    pageType,
    teamName,
    analytics,
    isMeSuccess,
    isOrganizationSuccess,
    shouldNotHaveAUser,
  ]);

  useEffect(() => {
    if (
      !isPageDetailsReady(PageDetails, analytics) ||
      eventPropertiesHasUndefinedValue(eventProperties)
    ) {
      return;
    }

    trackPage({
      analytics,
      page: PageDetails,
      eventProperties,
      options,
      fingerprint,
    });
  }, [PageDetails, eventProperties, options, analytics, fingerprint]);

  // There is a case where we will be loading organizationId and userId such that we will be sending undefined tracking.
  // Should we not load the page until they are fetched?
  // Ideal solution is to batch these track actions for when we have the data

  return (
    <PageContext.Provider value={PageDetails}>{children}</PageContext.Provider>
  );
};

const isPageDetailsReady = (
  pageDetails: PageContextType,
  analytics: AnalyticsContextType | null,
): boolean => {
  return (
    pageDetails.organizationId !== undefined &&
    pageDetails.userId !== undefined &&
    analytics?.circleciAnonymousId !== null
  );
};

const trackPage = ({
  analytics,
  page,
  eventProperties = {},
  options = {},
  fingerprint,
}: {
  analytics?: AnalyticsContextType | null;
  page: PageContextType;
  eventProperties?: Partial<EventProperties>;
  options?: PageOptions;
  fingerprint: string | null;
}): void => {
  if (page.pageName === null) {
    return;
  }
  const buildId: string | null =
    (window as any)?.next?.router?.pageLoader?.buildId ?? null;
  const properties: Partial<EventProperties> = {
    ...page,
    ...eventProperties,
    action: 'viewed',
    buildId,
    windowWidth: global?.innerWidth,
    windowHeight: global?.innerHeight,
    path: global?.location?.pathname,
    referrer: global?.document?.referrer,
    search: global?.location?.search,
    title: global?.document?.title,
    url: global?.location?.href,
    circleciAnonymousId: analytics?.circleciAnonymousId,
    googleClientId: analytics?.googleClientId,
    isGrowthAnalytics: false,
    browserFingerprint: fingerprint,
  };
  try {
    const propertiesSnaked =
      mapCamelCaseObjectToSnakeCase<EventPropertiesSnakeCase>(properties);
    if (page.anonymousId === undefined && options?.server) {
      analytics?.server?.page(page.pageName, {
        ...propertiesSnaked,
        is_growth_analytics: true,
      });
    }
    if (page.anonymousId !== undefined) {
      analytics?.segment?.page(page.pageName, propertiesSnaked);
    }
  } catch (error: any) {
    exceptionLogger().error(
      'Track Page',
      page.pageName,
      properties || {},
      error.message,
    );

    if (process.env.NODE_ENV === 'development') {
      throw error;
    }
  }
};

export default Page;
